import * as THREE from 'three';
import { PlanogramRenderer } from './PlanogramRenderer';
import { AttributeHelper } from './Helpers';

export class PlanogramImage extends HTMLElement{

    #uid = `image-${Math.random().toString(36).substring(2, 9)}`;
    /**@type {PlanogramRenderer} */
    #renderer = null;
    #firstRender = true;
    /**@type {Element} */
    #parent = null;
    get uid (){ return this.#uid; }

    /**@type {string|null} */
    type = null;

    /**@type {ImageData|null} */
    data = null;
    static attributes = {  
        Src: "src",
        Placement: "placement"
    };

    static observedAttributes = Object.values(PlanogramImage.attributes);

    #helper = new AttributeHelper(this);

    get src(){ return this.getAttribute(PlanogramImage.attributes.Src); }
    get placement(){ return this.#helper.enumFromAttribute(PlanogramImage.attributes.Placement, PlanogramImage.sides, PlanogramImage.sides.Front); }
    get depth(){ return this.#renderer.resources.getImageController(this.type).getImageDepth(this); }

    connectedCallback(){
        this.#renderer = PlanogramRenderer.get(this);
        this.#parent = this.parentElement;
        this.type = (() => {
            if(this.parentElement.tagName.toLocaleLowerCase() === "planogram-subcomponent") return PlanogramImage.type.Subcomponent;
            if(this.parentElement.tagName.toLocaleLowerCase() === "planogram-position-cap") return PlanogramImage.type.Position;
        })();
        if(!this.type) this.remove();
        
        this.#firstRender = false;
        this.setUpData();
    }

    attributeChangedCallback(name, oldValue, newValue){
        if (this.#firstRender || oldValue === newValue) return;
        if(name === PlanogramImage.attributes.Src) this.setUpData();
    }

    disconnectedCallback(){
        this.#renderer.resources.getImageController(this.type).removeImage(this);
        this.updateParent();
    }

    updateParent(){
        if(this.type === PlanogramImage.type.Subcomponent || this.type === PlanogramImage.type.Position)
            this.#parent.imagePlug();
    }

    async setUpData(){
        try{
            const data = await new Promise((resolve,reject)=>{
                const image = new Image();
                image.src = this.src;
                image.crossOrigin = "use-credentials";
                image.onload = () =>{
                    const {width, height} = PlanogramImageController.maxRestriction;
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.width = width;
                    canvas.height = height;
                    context.drawImage(image, 0, 0, width, height);
                    console.info( `Image loaded: \t\nFace:${this.placement}\t\nSrc:${this.src}`);
                    resolve(context.getImageData(0, 0, width, height).data);
                }
                image.onerror = ()=>{reject( `\t\nFace:${this.placement}\t\nSrc:${this.src}` )};
            });
            this.data = data;
            this.#renderer.resources.getImageController(this.type).updateImage(this);
            this.updateParent();
        }catch(e){
            console.error("Error loading image data", e );
        }
    }

    updateImageController(){
        const renderer = PlanogramRenderer.get(this);
        renderer.resources.getImageController(this.type).updateData();
    }

    static type = {
        Subcomponent: "subcomponent",
        Position: "position"
    }
    static sides = {
        Right: "right",
        Left: "left",
        Top: "top",
        Bottom: "bottom",
        Front: "front",
        Back: "back"
    }
}

export class PlanogramImageController {

    /**@type {THREE.DataArrayTexture} */
    #texture;
    /**@type {Map<string, PlanogramImage>} */
    #map = new Map();
    /**@type {THREE.ShaderMaterial} */
    #shader = null;

    #currentTextureDepth = 10;
    #depthIncrement = 10;

    get texture(){ return this.#texture; }

    /**
     * 
     * @param {THREE.ShaderMaterial} shader 
     */
    constructor( shader ){ 
        this.#shader = shader;
        this.updateData();
    }

    /**
     * 
     * @param {PlanogramImage} planImage 
     */
    removeImage( planImage ){
        if(!this.#map.has(planImage.uid)) return;
        this.#map.delete(planImage.uid);
        this.updateData();
    }

    updateData(){
        const values = [...this.#map.values()].filter(x=>x.data !== null);
        const { width, height } = PlanogramImageController.maxRestriction;

        if(!this.#texture){

            this.#texture = new THREE.DataArrayTexture(new Uint8Array(width * height * this.#currentTextureDepth * 4), width, height, this.#currentTextureDepth );
            this.#texture.format = THREE.RGBAFormat;
            this.#texture.type = THREE.UnsignedByteType;
            this.#shader.uniforms = {image:{value:this.#texture}};

        }else if(values.length > this.#currentTextureDepth){

            this.#currentTextureDepth += this.#depthIncrement;
            const depth = this.#currentTextureDepth;
            const data = values.reduce((acc, item, index)=>{ 
                acc.set(item.data, index * 4 * width * height);
                return acc;
            },new Uint8Array( width * height * depth * 4));
            
            this.#texture = new THREE.DataArrayTexture( data, width, height, depth );
            this.#texture.format = THREE.RGBAFormat;
            this.#texture.type = THREE.UnsignedByteType;

            this.#shader.uniforms.image.value = this.#texture;

        }
        if( values.length > 0 ){

            const depth = values.length;
            const data = values.reduce((acc, item, index)=>{ 
                acc.set(item.data, index * 4 * width * height);
                return acc;
            },new Uint8Array( width * height * this.#currentTextureDepth * 4));
            this.#texture.image = { data, width, height, depth };

        } 
        this.#texture.needsUpdate = true;
    }

    updateImage( planogramImage ){
        if(!this.#map.has(planogramImage.uid))
            this.#map.set(planogramImage.uid, planogramImage);
        this.updateData();
    }

    /**
     * 
     * @param {PlanogramImage} planImage 
     * @returns {number|null}
     */
    getImageDepth(planImage) {
        const values = [...this.#map.values()].filter(x=>x.data !== null);
        return values.findIndex(x=>x.uid === planImage.uid);
    }

    static maxRestriction = {
        width: 512,
        height: 512
    } 

}